import { Box, Link } from '@mui/material'

const PrivacyPolicy = () => {
  return (
    <Box>
      <p
        className="text-sm leading-normal font-family-georgia"
        sx={{ mb: 2, color: 'text.secondary' }}
      >
        This privacy statement was last updated in October 2025.
      </p>

      {/* Introduction */}
      <Box className="space-y-4">
        <div className="text-base pt-4 leading-normal font-bold font-family-georgia italic">
          Introduction
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          We are strongly committed to protecting personal data. This privacy statement describes why and how we collect, process and use personal data and provides information about individual’s rights in relation to personal data. It applies to personal data provided to us, both by individuals themselves and by others. We may use personal data provided to us for any of the purposes described in this privacy statement or as otherwise stated at the point of collection.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          You have been given access to this Application solely in connection with your role at your organization (your organization). You may be a partner, member or employee of your organization or you may be a contractor engaged by your organization to provide it with services. Your employment or other agreement with your organization regarding your role applies to your use of this Application.
        </p>

        {/* <p className="text-sm leading-normal font-family-georgia">
          By using this Application and providing personal information to us,
          you acknowledge you have read this privacy statement.
        </p> */}
      </Box>

      {/* Definitions */}
      <Box className="space-y-4">
        <div className="text-base pt-4 leading-normal font-bold font-family-georgia italic">
          Definitions
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          In this privacy statement we use the following terms:
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          <strong className="italic">Application</strong> refers to the Navigate
          Withholding Tax Solution
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          <strong className="italic">
            Personal data or personal information
          </strong>{' '}
          is how we refer to information about you or information that identifies you as “personal data” or “personal information”.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          <strong className="italic">Processing</strong> is how we sometimes refer to the handling, sharing, processing, protecting or storing of your personal data. We process personal data in this Application for numerous purposes, as set out below.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          <strong className="italic">Definition of "us", "we" and "our"</strong>
          <br />
          As used in this privacy statement, “us”, “we” and “our” refers to the PwC network and/or one or more of its Member Firms that may process your personal information. Each Member Firm in the PwC network is a separate legal entity. See http:{' '}
          <Link
            href="https://www.pwc.com/gx/en/about/office-locations.html"
            target="_blank"
          >
            https://www.pwc.com/gx/en/about/office-locations.html
          </Link>{' '}
          for a list of countries and regions in which PwC Member Firms operate.
        </p>
      </Box>

      {/* Processing of personal data */}
      <Box className="space-y-4">
        <div className="text-base pt-4 leading-normal font-bold font-family-georgia italic">
          Processing of personal data
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          Our policy is to process only the personal data that we need and be transparent about why and how we process personal data.        
        </p>
        <p className="text-sm leading-normal font-family-georgia">
          The categories of personal data processed by us in relation to this Application are generally:{' '}
        </p>
        <ul className="list-disc list-inside ">
          <li className="text-sm leading-normal font-family-georgia">Name</li>
          <li className="text-sm leading-normal font-family-georgia">Email ID</li>
        </ul>
        <p className="text-sm leading-normal font-family-georgia">
          Information about you when you use this Application, for example, logs of your activities (for example, when you signed in and signed out, when you upload or view a document, or when you post comments); logs and text you post in a comment, or logs showing what information you shared information, etc.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          You may choose to provide additional information when you use this Application, including any content you contribute.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          We do not intend to process special categories of personal information through this Application and no special categories personal information is required for this Application to operate.  Special categories of personal information include race or ethnic origin; political opinions; religious or philosophical beliefs; trade union membership; physical or mental health; genetic data; biometric data; sexual life or sexual orientation; and criminal records. We ask that you not provide information of this nature about yourself or anyone else via this Application. 
        </p>
      </Box>

      {/* Our legal grounds for processing your personal data */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Our legal grounds for processing your personal data{' '}
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          We rely on one or more of the following processing grounds (to the extent permitted by applicable law), or other grounds permitted by applicable law:
        </p>

        <ul className="list-disc pl-5">
          <li className="text-sm leading-normal font-family-georgia">
            Your free, specific, informed, unconditional and unambiguous consent communicated with a clear affirmative action for a specified purpose.
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            Our legitimate interests in the effective delivery of information and services to you and in the effective and lawful operation of our businesses and the legitimate interests of our clients in receiving professional services from us as part of running their organization (provided these do not interfere with your rights);
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            Our legitimate interests in developing and improving our businesses, services and offerings and in developing our new technologies and offerings (provided these do not interfere with your rights)
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            0ur legitimate interests in maintaining the security of our and our client’s data, the quality of our services and maintaining our reputation.
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            To satisfy any requirement of law; or,
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            To perform our obligations under a contractual arrangement with you
          </li>
        </ul>
      </Box>

      {/* Use of personal data */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Use of personal data
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          When you provide personally identifiable information to us, we use it for the purposes for which it was provided to us as stated at the point where the personal data is ingested into the Application (or as obvious from the context provided at such point). We may also use your data in order to administer and manage the Application or to communicate with you in relation to the Application and get notifications or updates.  The Application does not store personally identifiable information for dissemination or sale to outside parties for consumer marketing purposes, or host mailings.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          We may use your personal information for any of the purposes described in this privacy statement, as stated at the point of where the personal data is ingested into the Application, or as obvious from the context of provided at such point, including complying with any requirements of law, regulation or a professional body of which we are a member.
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          As with any provider of professional services, we are subject to legal, regulatory and professional obligations. We need to keep certain records to demonstrate that our services are provided in compliance with those obligations and those records may contain personal data. 
        </p>
      </Box>

      {/* Security */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Security
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          We have implemented generally accepted standards of technology and operational security in order to protect personal information from loss, misuse, alteration or destruction. Only authorized persons are provided with access to personal information; such individuals have agreed to maintain the confidentiality of this information. Although we use appropriate security measures once we have received your personal data, the transmission of data over the internet (including by e-mail) is never completely secure. We endeavor to protect personal data, but we cannot guarantee the security of data transmitted to or by us. 
        </p>
      
      </Box>

      {/* Cookies */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Cookies
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          This Application store cookies.
        </p>
      </Box>

      {/* Transfer of personal data */}
      <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
        Transfer of personal data
      </div>

      {/* Cross-border transfers */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Cross-border transfers
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          If we process your personal information, your personal information may be transferred to and stored outside the country where you are located, to the extent permitted by law, including countries outside the European Economic Area (EEA), where applicable.
        </p>
      </Box>

      {/* Other PwC Member Firms */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Other PwC Member Firms
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          For details of PwC Member Firm locations, please see{' '}
          <Link
            href="https://www.pwc.com/gx/en/about/office-locations.html"
            target="_blank"
          >
            http://www.pwc.com/gx/en/about/office-locations.html
          </Link>
          {' '}
          All PwC members firms are a separate legal entities.
        </p>
      
        <p className="text-sm leading-normal font-family-georgia">
          We may share personal data with other PwC Member Firms where necessary in connection with the purposes described in this privacy statement, to the extent permitted by law. For example, when providing professional services to a client we may share personal information with PwC Member Firms in different territories that are involved in providing advice to that client. 
        </p>
      </Box>

      {/* Third Party Providers */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Third Party Providers
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          We may transfer or disclose the personal data shared with us, to third party contractors, subcontractors, and/or their subsidiaries and affiliates. Third parties support the PwC Network in providing its services and help provide, run and manage IT systems. Examples of third-party contractors we use are providers of identity management, website hosting and management, data analysis, data backup, security and cloud storage services. The servers powering and facilitating our IT infrastructure are located in secure data centers around the world, and personal data may be stored in any one of them.  
        </p>

        <p className="text-sm leading-normal font-family-georgia">
          The third-party providers may use their own third-party subcontractors that have access to personal data (sub-processors). It is our policy to use only third-party providers that are bound to maintain appropriate levels of security and confidentiality, to process personal information only as instructed by us, and to carry those same obligations down to their sub-processors.
        </p>
      </Box>

      {/* Other disclosures */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Other disclosures
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          We may also disclose personal information under the following circumstances:
        </p>
        <ul className="list-disc pl-5">
          <li className="text-sm leading-normal font-family-georgia">
            With professional advisers, for example, law firms, as necessary to establish, exercise or defend our legal rights and obtain advice in connection with the running of our business. Personal data may be shared with these advisers as necessary in connection with the services they have been engaged to provide;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            When explicitly requested by you;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
             When required to deliver publications or reference materials requested by you;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            When required to facilitate conferences or events hosted by a third party;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            To law enforcement, regulatory and other government agencies and to professional bodies, as required by and/or in accordance with applicable law or regulation. We may also review and use your personal information to determine whether disclosure is required or permitted;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            When such disclosure or transfer is otherwise permitted by applicable law
          </li>
        </ul>
      </Box>

      {/* Retention of personal data */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Retention of personal data
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          "To the extent that we process your personal data as a data fiduciary, we shall, unless retention is necessary for compliance with any law for the time being in force, erase personal data upon you withdrawing your consent, or as soon as it is reasonable to assume that the specified purpose is no longer being served, whichever is earlier. Furthermore, you also have the corresponding right to ask for such erasure, unless retention of the same is necessary for the specified purpose or for compliance with any law for the time being in force”.
        </p>
      </Box>

      {/* Individuals' rights and how to exercise them */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Individuals’ rights and how to exercise them
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          You may have certain rights under laws of India in relation to the personal information we hold about you. In particular, you may have a legal right to:
        </p>
        <ul className="list-disc pl-5">
          <li className="text-sm leading-normal font-family-georgia">
            The right to access information about your personal data, including a summary of personal data which is being processed and the processing activities undertaken with respect to such personal data;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            the right to correction, completion, updating and erasure of your personal data for the processing of which you has previously given consent;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            The right to have your personal data erased by us, in accordance with law;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            The right to nominate any other individual who shall in the event of your death or incapacity exercise your rights in accordance with law;
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            The right to withdraw consent Where we process personal data based on consent, individuals have a right to withdraw consent at any time by application users by clicking
            <Link
              href="https://privacyportal-eu.onetrust.com/webform/f13f2198-97ab-4c25-a5cd-0fca8ada2e21/b4e47c5a-ca49-419b-85de-0e1f7f6457eb"
              target="_blank"
              className="pl-2"
            >
              here
            </Link>
          </li>
          <li className="text-sm leading-normal font-family-georgia">
            The right to a readily available means of grievance redressal, please submit your request at
            {' '}
             <Link href="mailto:in_privacy.in@pwc.com">
              in_privacy.in@pwc.com
            </Link>
          </li>
        </ul>
      </Box>

      {/* Complaints */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Complaints
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          In the event you have any grievance in relation to the processing of your personal data or your rights, you may reach out to
          <Link href="mailto:in_privacy.in@pwc.com">
            in_privacy.in@pwc.com
          </Link>
        </p>
      </Box>

      {/* Changes to this privacy statement */}
      <Box className="space-y-4">
        <div className="text-sm leading-normal font-family-georgia pt-4 italic font-bold">
          Changes to this privacy statement
        </div>
        <p className="text-sm leading-normal font-family-georgia">
          This privacy statement was last updated in October 2025.
        </p>
        <p className="text-sm leading-normal font-family-georgia">
          We may update this privacy statement at any times by publishing an updated version here. So you know when we make changes to this privacy statement, we will amend the revision date at the top of this page. The new modified or amended privacy statement will apply from that revision date. Therefore, we encourage you to review this privacy statement periodically to be information about how we are protecting your information. 
        </p>
        <p className="text-sm leading-normal font-family-georgia">
          By clicking on the “I agree” button below, you hereby provide your free, specific, informed, unconditional and unambiguous consent to the processing of your personal data as set out above.
        </p>
      </Box>
    </Box>
  )
}

export default PrivacyPolicy
